<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />
      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <v-toolbar-title v-else class="headline font-weight-bold"
        >Licence Providers ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>
      <v-btn
        v-show="!loading && isFiltered"
        rounded
        color="error lighten-1"
        class="mr-6"
        @click="clearFilters"
      >
        <span class="white--text mb-1">Clear Filter</span>
        <v-icon right>
          {{ icons.cancel }}
        </v-icon>
      </v-btn>
      <v-row v-show="showSearch" align="center" style="max-width: 300px">
        <v-text-field
          v-if="!loading"
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search provider name"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn class="ml-5" icon @click="showSearch = !showSearch">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>

        <v-btn
          depressed
          color="primary"
          @click="$router.push({ name: 'providers.add' })"
        >
          <v-icon>{{ icons.plus }}</v-icon>
          <span>Add Licence Provider</span>
        </v-btn>
      </template>
    </v-app-bar>

    <div
      class="mt-12 mx-4"
      id="providers-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
    >
      <v-simple-table>
        <thead>
          <tr>
            <th width="25%" class="text-left pa-0">
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    Provider Name
                    <v-icon>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-list :min-width="230">
                  <v-radio-group v-model="sort" hide-details class="mt-0 py-0">
                    <div v-for="(item, key) in providerNameSort" :key="key">
                      <v-list-item inactive>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                        <v-list-item-action @click="updateFetchProviders">
                          <v-radio :value="item.value" />
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="key !== providerNameSort.length - 1" />
                    </div>
                  </v-radio-group>
                </v-list>
              </v-menu>
            </th>
            <th width="25%" class="text-left">
              <v-menu offset-y>
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    Template
                    <v-icon right>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-list :min-width="230">
                  <v-radio-group
                    v-model="filter.template"
                    hide-details
                    class="mt-0 py-0"
                  >
                    <div v-for="(filter, key) in templatesFilters" :key="key">
                      <v-list-item inactive>
                        <v-list-item-title>{{
                          filter.label
                        }}</v-list-item-title>
                        <v-list-item-action @click="updateFetchProviders">
                          <v-radio :value="key + 1" />
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="key !== templatesFilters.length - 1" />
                    </div>
                  </v-radio-group>
                </v-list>
              </v-menu>
            </th>
            <th width="25%" class="text-left">
              <v-menu
                v-model="datePicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    Date Added
                    <v-icon>
                      {{ icons.menu }}
                    </v-icon>
                  </div>
                </template>
                <v-date-picker
                  v-model="selectedDate"
                  @change="filterDateProviders"
                ></v-date-picker>
              </v-menu>
            </th>
            <th width="25%" class="text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="provider in providers"
            :key="provider.id"
            class="clickable"
            @click="
              $router.push({
                name: 'provider.details',
                params: { id: provider.id },
              })
            "
          >
            <td class="text-left pl-0">
              {{ provider.name }}
            </td>
            <td>{{ categoryTemplates(provider.categories) }}</td>
            <td>{{ provider.addedDate }}</td>
            <td>
              <v-btn color="error" @click.stop="showDeleteDialog(provider.id)"
                >Delete</v-btn
              >
            </td>
          </tr>

          <template v-if="loading">
            <tr v-for="index in 10" :key="index + '-skeleton'">
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
              <td class="pl-0">
                <v-skeleton-loader
                  type="list-item"
                  transition="fade-transition"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </v-simple-table>

      <div
        class="d-flex justify-center mt-3"
        v-if="!(providers.length > 0) && !loading"
      >
        <p>No providers found.</p>
      </div>
    </div>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" right>
      {{ snackbar.message }}
      <v-btn color="white" text @click="snackbar.show = false">Ok</v-btn>
    </v-snackbar>
    <v-dialog v-model="deleteDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">Delete Provider</v-card-title>

        <v-card-text
          >Are you sure you want to delete this provider?</v-card-text
        >

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn text @click="deleteDialog = false">Cancel</v-btn>

          <v-btn color="red" text @click="deleteLicenceProvider()">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiMenuDown,
  mdiClose,
} from '@mdi/js'
import ControlsMixin from '@/utils/mixins/Controls'
import TemplatesMixin from '@/utils/mixins/Templates'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'
import { each } from 'lodash'

export default {
  name: 'ProvidersPage',

  mixins: [ControlsMixin, TemplatesMixin],

  directives: {
    infiniteScroll,
  },

  components: {
    AppBarNavIcon,
  },

  data() {
    return {
      loading: false,
      showSearch: true,
      sort: null,
      filter: {
        search: '',
        template: null,
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        menu: mdiMenuDown,
        cancel: mdiClose,
      },
      snackbar: {
        show: false,
        message: null,
        color: null,
      },
      deleteDialog: false,
      selectedProvider: null,
      dateSort: [
        { value: 'created_at', text: 'Ascending' },
        { value: '-created_at', text: 'Descending' },
      ],
      providerNameSort: [
        { value: 'name', text: 'Ascending' },
        { value: '-name', text: 'Descending' },
      ],
      datePicker: false,
      selectedDate: null,
      isFiltered: false,
    }
  },

  created() {
    this.clearProviders()
    this.fetchProviders(1)
  },

  computed: {
    ...mapState({
      providers: (state) => state.provider.list,
      listMeta: (state) => state.provider.listMeta,
    }),
    templatesFilters() {
      let templates = this.templates
      templates.push({ label: 'All' })
      return templates
    },
  },

  methods: {
    ...mapActions({
      getProviders: 'provider/getProviders',
      deleteProvider: 'provider/deleteProvider',
    }),

    ...mapMutations({
      clearProviders: 'provider/clearProviderList',
    }),

    categoryTemplates(categories) {
      const templates = []
      each(categories, (category) => {
        templates.push(category.label)
      })
      return templates.join()
    },

    pageChanged(page) {
      this.fetchProviders(page)
    },

    loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        this.fetchProviders(this.listMeta.current_page + 1)
      }
    },

    search: debounce(function() {
      this.clearProviders()
      this.fetchProviders()
    }, 600),

    async fetchProviders(page = 1) {
      if (this.loading) return
      let params = { page }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      if (this.sort) {
        params.sort = this.sort
      }
      if (this.selectedDate) {
        params.searchDate = this.selectedDate
      }
      if (this.filter.template) {
        params.template =
          this.filter.template !== 11 ? this.filter.template : null
      }
      this.loading = true
      await this.getProviders(params)
      this.loading = false
    },

    filterDateProviders: debounce(function() {
      this.isFiltered = true
      this.datePicker = false
      this.clearProviders()
      this.fetchProviders()
    }, 600),

    updateFetchProviders() {
      this.isFiltered = true
      this.clearProviders()
      this.fetchProviders()
    },

    clearFilters() {
      this.sort = null
      this.selectedDate = null
      this.filter.template = null
      this.isFiltered = false
      this.clearProviders()
      this.fetchProviders()
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },
    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },

    showDeleteDialog(id) {
      this.deleteDialog = true
      this.selectedProvider = id
    },

    async deleteLicenceProvider() {
      await this.deleteProvider(this.selectedProvider).then(() => {
        this.deleteDialog = false
        this.showSnackbar('Provider deleted successfully!', 'success')
        this.clearProviders()
        this.fetchProviders(1)
      })
    },
  },
}
</script>
